import React, { useEffect } from "react";
import { Box, Button, Grid } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import AppForm from "components/Shared/AppForm";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useNavigate } from "react-router-dom";
import { formHookInputHelper } from "utils/formHookField";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { emailValidation } from "utils/validation";

const VendorForm = ({
  vendorDetail = null,
  isBusy = false,
  onSubmit = () => { },
}) => {
  const customerTypeEnums = ["Individual", "Business"];
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      address: "",
      type: "Individual",
      controllingUser: vendorDetail?.controllingUser,
    },
  });

  const [
    fetchUsers,
    { data: userList = [], isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLSystemUserListQuery();

  const loading = isBusy || loadingUsers || fetchingUsers;

  const handlePayload = (data) => {
    const _payload = {
      ...data,
      controllingUser: data?.controllingUser?._id,
    };
    onSubmit(_payload);
  };

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (vendorDetail) {
      reset({
        name: vendorDetail.name ?? "",
        phone: vendorDetail.phone ?? "",
        email: vendorDetail.email ?? "",
        address: vendorDetail.address ?? "",
        type: vendorDetail.type ?? "Individual",
        controllingUser:
          {
            name: vendorDetail?.controllingUser?.name,
            id: vendorDetail?.controllingUser?._id,
          } ?? "",
      });
    }
  }, [vendorDetail, reset]);

  return (
    <AppForm loading={loading}>
      <AppForm.Content>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Please provide full Name." }}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  required
                  fullWidth
                  label="Full Name"
                  placeholder="Enter Full Name"
                  error={!!errors?.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="email"
              control={control}
              rules={emailValidation}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: "Phone is required",
                validate: (value) => {
                  const digits = value.replace(/\D/g, "");
                  if (digits.length < 7 || digits.length > 17) {
                    return "Phone number must be between 9 and 16 digits";
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <CustomCountryPhoneInput
                  placeholder="Phone Number"
                  label="Phone*"
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="address"
              control={control}
              rules={{ required: "Please provide address." }}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  required
                  fullWidth
                  label="Address"
                  placeholder="Enter Address"
                  error={!!errors?.address}
                  helperText={errors?.address?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="controllingUser"
              control={control}
              rules={{ required: "Please select a controlling user." }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Controlling User"
                  loading={loadingUsers || fetchingUsers}
                  loadingText="Loading Users..."
                  options={userList || []}
                  value={field.value || null}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  error={!!errors?.controllingUser}
                  helperText={errors?.controllingUser?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Please select a type." }}
              render={(props) => (
                <BaseSelect
                  {...formHookInputHelper(props)}
                  fullWidth
                  required
                  label="Type"
                  options={customerTypeEnums}
                />
              )}
            />
          </Grid>
        </Grid>

        <Box
          paddingY={2}
          gap={2}
          style={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button disabled={isBusy} onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isBusy}
            onClick={handleSubmit(handlePayload)}
          >
            {vendorDetail ? "Update" : "Add"}
          </Button>
        </Box>
      </AppForm.Content>
    </AppForm>
  );
};

export default VendorForm;